import styled from '@emotion/styled';

import { Button } from '../Button';
import { Message } from '../Message';

import type { ReviewResultsProps, ReviewStatus } from './ReviewResults.types';

// EXACT styled components from ResultsSummary.tsx

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const SummaryContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const SummaryHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
`;

const TitleSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ReviewType = styled.span`
  ${({ theme }) => `
    font-size: 10px;
    font-weight: ${theme.typography.fontWeight.semibold};
    color: ${theme.colors.text.muted};
    text-transform: uppercase;
    letter-spacing: 0.5px;
  `}
`;

const ReviewTitle = styled.h3`
  ${({ theme }) => `
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: ${theme.typography.fontSize.md};
    font-weight: ${theme.typography.fontWeight.semibold};
    color: ${theme.colors.text.primary};
    margin: 0;
  `}
`;

const StatusBadge = styled.span<{ $status: ReviewStatus; $hasRisks: boolean }>`
  ${({ theme, $status, $hasRisks }) => `
    display: inline-flex;
    align-items: center;
    padding: 4px 10px;
    font-size: 11px;
    font-weight: ${theme.typography.fontWeight.semibold};
    border-radius: 16px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    white-space: nowrap;
    
    background-color: ${
      $status === 'processing'
        ? '#FFF3E0'
        : $status === 'failed'
          ? '#FFEBEE'
          : $hasRisks
            ? '#FFF8E1'
            : '#E8F5E9'
    };
    
    color: ${
      $status === 'processing'
        ? '#E65100'
        : $status === 'failed'
          ? '#C62828'
          : $hasRisks
            ? '#F57C00'
            : '#2E7D32'
    };
  `}
`;

const SummaryGrid = styled.div`
  ${({ theme }) => `
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
    padding: 12px;
    background-color: ${theme.colors.background.default};
    border-radius: 8px;
  `}
`;

const SummaryItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const SummaryLabel = styled.span`
  ${({ theme }) => `
    font-size: 10px;
    font-weight: ${theme.typography.fontWeight.medium};
    color: ${theme.colors.text.muted};
    text-transform: uppercase;
    letter-spacing: 0.5px;
  `}
`;

const SummaryValue = styled.span`
  ${({ theme }) => `
    font-size: ${theme.typography.fontSize.sm};
    font-weight: ${theme.typography.fontWeight.medium};
    color: ${theme.colors.text.primary};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  `}
`;

const RisksMessageContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const RisksCount = styled.span`
  ${({ theme }) => `
    font-weight: ${theme.typography.fontWeight.semibold};
  `}
`;

const RisksSubtext = styled.span`
  ${({ theme }) => `
    font-size: ${theme.typography.fontSize.xs};
    opacity: 0.85;
  `}
`;

// EXACT styled components from ResultActions.tsx

const ActionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const NoActionsMessage = styled.div`
  ${({ theme }) => `
    text-align: center;
    padding: 12px;
    color: ${theme.colors.text.muted};
    font-size: ${theme.typography.fontSize.sm};
  `}
`;

/**
 * Formats a timestamp for display - EXACT from ResultsSummary.tsx
 */
function formatTimestamp(timestamp: string): string {
  const date = new Date(timestamp);
  return date.toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
}

/**
 * Gets display text for review status - EXACT from ResultsSummary.tsx
 */
function getStatusText(status: ReviewStatus, hasRisks: boolean): string {
  switch (status) {
    case 'processing':
      return 'Processing';
    case 'failed':
      return 'Failed';
    case 'completed':
      return hasRisks ? 'Risks Detected' : 'No Risk Detected';
    default:
      return status;
  }
}

/**
 * ReviewResults component - Combined ResultsSummary + ResultActions
 *
 * EXACT copy of styling from web-app-risk-reviews
 *
 * Displays a summary of the compliance review including:
 * - Overall status with color-coded badge (pass/fail/warning)
 * - Review timestamp
 * - File name (for file reviews)
 * - Number of risks with visual indicator
 * - Action buttons (Send to IntelligenceBank)
 *
 * Status colors:
 * - Green: Completed with no risks detected
 * - Orange/Yellow: Completed with risks detected
 * - Orange: Processing
 * - Red: Failed
 *
 * @example
 * ```tsx
 * <ReviewResults
 *   results={reviewResult}
 *   reviewType="file"
 *   onSendToIB={handleSendToIB}
 *   canSendToIB={true}
 *   showActions
 * />
 * ```
 */
export const ReviewResults: React.FC<ReviewResultsProps> = ({
  results,
  reviewType,
  onSendToIB,
  canSendToIB = true,
  isLoading = false,
  showActions = true,
  className,
  dataTestId,
  dataId,
  style,
}) => {
  // Defensive check: risks might be undefined after storage restoration
  const risks = results.risks ?? [];
  const risksCount = risks.length;
  const hasRisks = risksCount > 0;

  return (
    <Container className={className} data-testid={dataTestId} data-id={dataId} style={style}>
      {/* Summary Section - EXACT from ResultsSummary.tsx */}
      <SummaryContainer>
        <SummaryHeader>
          <TitleSection>
            <ReviewType>Risk Review</ReviewType>
            <ReviewTitle>
              {results.type === 'file' || results.type === 'weburl'
                ? 'File Risk Review'
                : 'Text Risk Review'}
            </ReviewTitle>
          </TitleSection>
          <StatusBadge $status={results.status} $hasRisks={hasRisks}>
            {getStatusText(results.status, hasRisks)}
          </StatusBadge>
        </SummaryHeader>

        <SummaryGrid>
          {results.fileName && (
            <SummaryItem>
              <SummaryLabel>File</SummaryLabel>
              <SummaryValue>{results.fileName}</SummaryValue>
            </SummaryItem>
          )}

          <SummaryItem>
            <SummaryLabel>Reviewed</SummaryLabel>
            <SummaryValue>{formatTimestamp(results.timestamp)}</SummaryValue>
          </SummaryItem>

          <SummaryItem>
            <SummaryLabel>Review ID</SummaryLabel>
            <SummaryValue>{results.reviewId}</SummaryValue>
          </SummaryItem>
        </SummaryGrid>

        <Message variant={hasRisks ? 'warning' : 'success'} mode="banner">
          <RisksMessageContent>
            <RisksCount>
              {risksCount === 0
                ? 'No Risk detected'
                : `${risksCount} risk${risksCount !== 1 ? 's' : ''} detected`}
            </RisksCount>
            <RisksSubtext>
              {risksCount === 0
                ? 'No Risks detected'
                : 'Review detected Risks below for details'}
            </RisksSubtext>
          </RisksMessageContent>
        </Message>
      </SummaryContainer>

      {/* Actions Section - EXACT from ResultActions.tsx */}
      {showActions && (
        <ActionsContainer>
          {reviewType === 'text' ? (
            <NoActionsMessage>Text Reviews cannot be sent to IntelligenceBank</NoActionsMessage>
          ) : (
            <Button
              variant="primary"
              size="md"
              fullWidth
              onClick={onSendToIB}
              disabled={isLoading || !canSendToIB}
            >
              Send to IntelligenceBank
            </Button>
          )}
        </ActionsContainer>
      )}
    </Container>
  );
};

ReviewResults.displayName = 'ReviewResults';

export default ReviewResults;
